// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';
import type { LegacyMatrixModelResponse, MatrixModel, Action } from '../../../store/engine/types';
import type { MatrixDimensions, UpdateMatrixSizeProps } from './types';
import { MatrixAxisOperation } from './types';
import type { Axis } from '../../../editor/components/NodeEditorDrawer/MatrixModel/AxisHeaderDropdown';
import type { MatrixCellChangeAddedDeleted, MatrixCellChangeModified } from '../../changelog/formatters';
import type { ModifyDirection } from '../../changelog/formatters/textBasedFormatters/templates';
import { generatePublicIdHash } from 'flow/service/publicIds';

export const getAxisOrder = (
  axisHeaderDataType: LegacyMatrixModelResponse[
    'x_axis_header_data_type' | 'y_axis_header_data_type'
  ],
  axisHeader: LegacyMatrixModelResponse[
    'x_axis_header' | 'y_axis_header'
  ],
) => {
  if (!axisHeader?.length) return null;
  if (axisHeaderDataType === 'string') return null;

  return (+axisHeader[0] < +axisHeader[1]
    ? 'asc'
    : 'desc'
  );
};

export function getDimensionOptions(): Option[] {
  return [
    { label: '1 Dimension (y axis)', value: '1' },
    { label: '2 Dimensions (x and y axes)', value: '2' },
  ];
}

export function getDataTypeOptions(): Option[] {
  return [
    { label: 'Integer', value: 'number' },
    { label: 'String', value: 'string' },
  ];
}

export function isMatrix1Dimensional(
  xAxisSettings: MatrixModel['axis']['x'],
): boolean {
  return !xAxisSettings.order
    && !xAxisSettings.axisHeaderDataType
    && !xAxisSettings.axisHeader.length;
}

export function getDimensionNumber(
  xAxisSettings: MatrixModel['axis']['x'],
): MatrixDimensions {
  return isMatrix1Dimensional(xAxisSettings) ? 1 : 2;
}

export const getColOrRow = (axis: Axis) => (
  axis === 'X' ? 'column' : 'row'
);

export const updateMatrixSize = ({
  axis, matrix, op, count = 1,
}: UpdateMatrixSizeProps) => {
  const group = getColOrRow(axis);
  const updateCount = op === MatrixAxisOperation.ADD ? count : -Math.abs(count);

  let updatedMatrix = [...matrix];
  switch (group) {
    case 'column': { // # of columns (length of inner array)
      if (op === MatrixAxisOperation.ADD) {
        // need to add n null to each inner array
        updatedMatrix = updatedMatrix.map(row => [
          ...row, ...Array(updateCount).fill(null),
        ]);
      } else if (op === MatrixAxisOperation.DELETE) {
        // need to remove last n elements of each inner array
        updatedMatrix = updatedMatrix.map(row => row.slice(0, updateCount));
      }
      return updatedMatrix;
    }
    case 'row': { // # of rows (# of inner arrays)
      if (op === MatrixAxisOperation.ADD) {
        // need to add n null * inner array length
        const newRows = [];
        for (let i = 0; i < updateCount; i += 1) {
          const newRow = new Array(matrix[0].length).fill(null);
          newRows.push(newRow);
        }
        updatedMatrix.push(...newRows);
      } else if (op === MatrixAxisOperation.DELETE) {
        // need to remove last n arrays
        updatedMatrix = updatedMatrix.slice(0, updateCount);
      }
      return updatedMatrix;
    }
    default:
      return matrix;
  }
};

export const insertOneRowToMatrix = (
  { rowIndex, matrix }:
  { rowIndex: number; matrix: MatrixModel['matrix'] },
) => {
  const updatedMatrix = [...matrix];
  // need to add n null * inner array length
  const newRow = new Array(matrix[0].length).fill(null);
  updatedMatrix.splice(rowIndex, 0, newRow);
  return updatedMatrix;
};

export const deleteOneRowFromMatrix = (
  { rowIndex, matrix }:
  { rowIndex: number; matrix: MatrixModel['matrix'] },
) => {
  const updatedMatrix = [...matrix];
  updatedMatrix.splice(rowIndex, 1);
  return updatedMatrix;
};

export function getCellLabel(col: number, row: number) {
  const alphabet = [...'abcdefghijklmnopqrstuvwxyz'];
  const colLabel = alphabet[col];
  const rowLabel = row + 1;
  return `${colLabel}${rowLabel}`;
}

export function getFormattedMatrixDataType(dataType: MatrixModel['matrixDataType']) {
  return dataType === 'number' ? 'integer' : 'string';
}

export function getCommaSeparatedMatrixValues(matrix: MatrixModel['matrix']): string {
  const labelValuePairArray: string[] = [];

  matrix.forEach((row, rowIndex) => {
    row.forEach((cellValue, colIndex) => {
      const cellLabel = getCellLabel(colIndex, rowIndex);
      labelValuePairArray.push(`${cellLabel}: ${cellValue}`);
    });
  });

  return labelValuePairArray.join(', ');
}

export function convertMappingToCommaSeparatedString(labelValueMap: {
  [cellLabel: string]: number | string;
}): string {
  const labelValueList = Object.entries(labelValueMap).map(
    ([cellLabel, cellValue]) => `${cellLabel}: ${cellValue}`,
  );

  return labelValueList.join(', ');
}

export function getMatrixCellLabelValueMap(matrix: MatrixModel['matrix']) {
  const labelValueMap = {};

  matrix.forEach((row, rowIndex) => {
    row.forEach((cellValue, colIndex) => {
      const cellLabel = getCellLabel(colIndex, rowIndex);
      labelValueMap[cellLabel] = cellValue;
    });
  });

  return labelValueMap;
}

export function getFormattedMatrixCellModifications(
  matrixChanges: MatrixCellChangeModified | MatrixCellChangeAddedDeleted,
  direction?: ModifyDirection,
) {
  const matrixChangeMapping: {
    [cellLabel: string]: number | string;
  } = {};

  /**
   * If the direction argument is provided, we assume this change is a modification,
   * and include prev/next subfields in the returned mapping.
  */
  if (direction) {
    Object.keys(matrixChanges).forEach(cellLabel => {
      const prevOrNext = direction === 'from'
        ? (matrixChanges as MatrixCellChangeModified)[cellLabel].prev
        : (matrixChanges as MatrixCellChangeModified)[cellLabel].next;
      matrixChangeMapping[cellLabel] = prevOrNext;
    });
  } else {
    Object.keys(matrixChanges).forEach(cellLabel => {
      matrixChangeMapping[cellLabel] = (matrixChanges as MatrixCellChangeAddedDeleted)[cellLabel];
    });
  }

  const sortedChangeMapping = Object.keys(matrixChangeMapping).sort().reduce(
    (sortedMapping, cellLabel) => {
      sortedMapping[cellLabel] = matrixChangeMapping[cellLabel];
      return sortedMapping;
    }, {});

  const formattedValue = convertMappingToCommaSeparatedString(sortedChangeMapping);

  return formattedValue;
}


export function matrixModelActionHasDataRoutes(action: Action) {
  return Boolean(action.matrixModel.dataRouteX) || Boolean(action.matrixModel.dataRouteY);
}

export function generateTemporaryMatrixModelToken(): string {
  return `TEMP-MM-${generatePublicIdHash(20)}`;
}
